import axios, { InternalAxiosRequestConfig } from 'axios';
import { ChatRequest, ChatResponse, FeedbackPayload } from '../types';
import { mockChatApi } from './mockApi';
import { msalInstance, silentRedirectUri } from '../msalInstance';
import { apiTokenRequest } from '../authConfig';
import { requiresInteractiveConsent, triggerConsentRedirect } from '../utils/apiAuthHelper';

// Base API URL - update this to match your backend URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 1200000, // 20 minutes to match backend job timeout
});

// Attach Azure AD Bearer token to every request when API scope is configured
apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (!apiTokenRequest) return config;

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return config;

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account: accounts[0],
      redirectUri: silentRedirectUri,
    });
    config.headers.Authorization = `Bearer ${response.accessToken}`;
  } catch (error) {
    if (requiresInteractiveConsent(error)) {
      await triggerConsentRedirect();
    } else {
      console.error('[Auth] Failed to acquire API token:', error instanceof Error ? error.message : error);
    }
  }
  return config;
});

// On 401 response, force-refresh the token and retry once; if still 401, trigger re-login
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Don't retry if this is an abort or if we've already retried
    if (error.name === 'AbortError' || config._retryCount >= 2) {
      return Promise.reject(error);
    }

    // 401: force-refresh token and retry once
    if (error.response?.status === 401 && apiTokenRequest && !config._authRetried) {
      config._authRetried = true;
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        try {
          const tokenResp = await msalInstance.acquireTokenSilent({
            ...apiTokenRequest,
            account: accounts[0],
            forceRefresh: true,
            redirectUri: silentRedirectUri,
          });
          config.headers.Authorization = `Bearer ${tokenResp.accessToken}`;
          return apiClient(config);
        } catch (refreshError) {
          if (requiresInteractiveConsent(refreshError)) {
            await triggerConsentRedirect();
          }
        }
      }
      return Promise.reject(error);
    }
    
    // Retry on network errors or 5xx server errors (common in microservices)
    if (
      !error.response || 
      error.code === 'NETWORK_ERROR' ||
      error.code === 'ECONNRESET' ||
      error.code === 'TIMEOUT' ||
      (error.response.status >= 500 && error.response.status <= 599)
    ) {
      config._retryCount = (config._retryCount || 0) + 1;
      console.warn(`[API_RETRY] Retrying request to ${config.url} (attempt ${config._retryCount})`);
      
      // Exponential backoff: 1s, 2s for retries
      await new Promise(resolve => setTimeout(resolve, 1000 * config._retryCount));
      
      return apiClient(config);
    }
    
    return Promise.reject(error);
  }
);

// Schema cache types
export interface SchemaStatusResponse {
  cached: boolean;
  generated_at?: string;
  age_days?: number;
  ttl_days?: number;
  is_fresh?: boolean;
  tables?: string[];
  sample_rows_per_table?: number;
  sampling_method?: string;
  expires_at?: string;
  message?: string;
  // Column-wise sample values per table: { tableName: { colName: [val0, val1, ...] } }
  schema_samples?: Record<string, Record<string, (string | number | null)[]>>;
}

export interface SchemaRefreshResponse {
  success: boolean;
  message: string;
  metadata: {
    generated_at: string;
    tables: string[];
    sample_rows_per_table: number;
  };
}

// Job status and progress types
export interface JobStatus {
  job_id: string;
  status: string;
  current_stage?: string;
  stage_message?: string;
  progress: number;
  error?: string;
  created_at: number;
  updated_at: number;
}

export interface JobCreationResponse {
  job_id: string;
  status: string;
  message: string;
}

// Progress callback type
export type ProgressCallback = (status: JobStatus) => void;

// Actual API implementation using polling pattern
const realChatApi = {
  // Create a new query job (returns immediately with 202 Accepted)
  createQueryJob: async (request: ChatRequest): Promise<JobCreationResponse> => {
    try {
      const response = await apiClient.post<JobCreationResponse>('/query', {
        message: request.message,
        conversation_id: request.conversationId,
        previous_user_message: request.previous_user_message,
        previous_assistant_response: request.previous_assistant_response,
        original_query: request.original_query,
        is_clarification: request.is_clarification || false,
        user_email: request.user_email,  // Pass user_email for logging
        enable_search_enrichment: request.enable_search_enrichment || false  // Dev toggle for search enrichment
      });
      
      return response.data;
    } catch (error) {
      console.error('Error creating query job:', error);
      throw error;
    }
  },

  // Get job status
  getJobStatus: async (jobId: string, signal?: AbortSignal): Promise<JobStatus> => {
    try {
      const response = await apiClient.get<JobStatus>(`/query/status/${jobId}`, {
        signal
      });
      return response.data;
    } catch (error) {
      console.error('Error getting job status:', error);
      throw error;
    }
  },

  // Get job result (when completed)
  getJobResult: async (jobId: string, signal?: AbortSignal): Promise<ChatResponse> => {
    try {
      const response = await apiClient.get<ChatResponse>(`/query/result/${jobId}`, {
        signal
      });
      return response.data;
    } catch (error) {
      console.error('Error getting job result:', error);
      throw error;
    }
  },

  // Cancel a running job
  cancelJob: async (jobId: string): Promise<boolean> => {
    try {
      const response = await apiClient.delete(`/query/cancel/${jobId}`);
      return response.status === 200;
    } catch (error) {
      console.error('Error canceling job:', error);
      throw error;
    }
  },

  // Poll a job until completion with proper error handling and timeout
  pollJobUntilComplete: async (
    jobId: string, 
    onProgress?: ProgressCallback,
    pollInterval: number = 2000,
    maxTimeout: number = 600000 * 2,
    signal?: AbortSignal
  ): Promise<ChatResponse> => {
    const startTime = Date.now();
    let currentInterval = pollInterval;
    let consecutiveFailures = 0;
    let responseReadyResult: ChatResponse | null = null;
    let responseReadyTimestamp: number | null = null;
    const citationPhaseTimeout = 180000; // 3 min max wait for citations after response_ready
    let pollCount = 0;

    console.log(`[POLLING] Starting job ${jobId} polling (interval: ${pollInterval}ms, timeout: ${maxTimeout}ms)`);

    while (Date.now() - startTime < maxTimeout) {
      pollCount++;
      
      // Check if we've been aborted
      if (signal?.aborted) {
        console.log(`[POLLING] Job ${jobId} polling aborted after ${pollCount} polls`);
        throw new DOMException('Polling was aborted', 'AbortError');
      }

      // Safety: if we have the response but citations are taking too long, stop polling
      if (responseReadyResult && responseReadyTimestamp &&
          Date.now() - responseReadyTimestamp > citationPhaseTimeout) {
        console.log(`[POLLING] Citation phase timed out after ${citationPhaseTimeout}ms, returning response without citations`);
        return responseReadyResult;
      }
      
        try {
          console.log(`[POLLING] Job ${jobId} poll #${pollCount} at ${Date.now() - startTime}ms`);
          const status = await realChatApi.getJobStatus(jobId, signal);
          consecutiveFailures = 0;
          
          console.log(`[POLLING] Job ${jobId} status: ${status.status}, stage: ${status.current_stage}, progress: ${status.progress}`);
          
          if (onProgress) {
            try {
              onProgress(status);
            } catch (progressError) {
              console.warn('Progress callback error:', progressError);
            }
          }

        // Check if job is response_ready (first phase complete)
        if (status.status === 'response_ready' && !responseReadyResult) {
          responseReadyResult = await realChatApi.getJobResult(jobId, signal);
          responseReadyTimestamp = Date.now();
          console.log('Response ready result received, continuing to poll for citations...');
          currentInterval = Math.max(pollInterval, 3000);
        }

        if (status.status === 'completed') {
          const finalResult = await realChatApi.getJobResult(jobId, signal);
          console.log('Citations completed, returning final result');
          return finalResult;
        }

        if (status.status === 'failed' || status.status === 'cancelled') {
          const errorMessage = status.status === 'cancelled' 
            ? 'Task was cancelled by user'
            : (status.error || 'Job failed with unknown error');
          throw new Error(errorMessage);
        }

        const elapsedTime = Date.now() - startTime;
        if (status.current_stage === 'sql' && elapsedTime > 60000) {
          currentInterval = Math.min(5000, currentInterval * 1.1);
        } else if (elapsedTime > 300000) {
          currentInterval = Math.min(10000, currentInterval * 1.2);
        } else if (status.status === 'response_ready') {
          currentInterval = Math.max(3000, pollInterval);
        } else {
          currentInterval = pollInterval;
        }

        const delayMs = currentInterval;
        await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(resolve, delayMs);
          if (signal) {
            const abortHandler = () => {
              clearTimeout(timeoutId);
              reject(new DOMException('Polling was aborted', 'AbortError'));
            };
            signal.addEventListener('abort', abortHandler, { once: true });
          }
        });
        
              } catch (error: any) {
        if (error.name === 'AbortError') {
          throw error;
        }
        
        consecutiveFailures++;
        console.warn(`[POLLING] Job ${jobId} poll #${pollCount} error (attempt ${consecutiveFailures}):`, error.message);
        
        if (consecutiveFailures >= 5) {
          console.error(`[POLLING] Job ${jobId} polling failed after ${consecutiveFailures} consecutive errors`);
          throw new Error(`Polling failed after ${consecutiveFailures} consecutive errors: ${error}`);
        }
        
        if (error.code === 'NETWORK_ERROR' || error.code === 'ECONNRESET' || error.code === 'TIMEOUT') {
          console.warn(`[POLLING] Network error detected for job ${jobId}, will retry with extended backoff`);
        }
        
        const backoffMs = Math.min(30000, pollInterval * Math.pow(2, consecutiveFailures));
        console.log(`[POLLING] Job ${jobId} waiting ${backoffMs}ms before retry`);
        await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(resolve, backoffMs);
          if (signal) {
            const abortHandler = () => {
              clearTimeout(timeoutId);
              reject(new DOMException('Polling was aborted', 'AbortError'));
            };
            signal.addEventListener('abort', abortHandler, { once: true });
          }
        });
      }
    }

    if (responseReadyResult) {
      console.log('[POLLING] Overall timeout but we have response_ready result, returning it');
      return responseReadyResult;
    }
    throw new Error('Job polling timed out');
  },

  // Combined method that handles the full polling lifecycle
  sendMessageWithPolling: async (
    request: ChatRequest, 
    onProgress?: ProgressCallback,
    signal?: AbortSignal
  ): Promise<ChatResponse> => {
    try {
      // Step 1: Create the job
      const jobCreation = await realChatApi.createQueryJob(request);
      const jobId = jobCreation.job_id;

      // Step 2: Poll until completion
      return await realChatApi.pollJobUntilComplete(jobId, onProgress, 2000, 600000*2, signal);
      
    } catch (error) {
      console.error('Error in polling workflow:', error);
      throw error;
    }
  },

  // Two-phase response method: returns early result when response_ready, continues polling for citations
  sendMessageWithTwoPhaseResponse: async (
    request: ChatRequest, 
    onProgress?: ProgressCallback,
    onResponseReady?: (response: ChatResponse) => void,
    onCitationsReady?: (response: ChatResponse) => void,
    signal?: AbortSignal
  ): Promise<ChatResponse> => {
    try {
      // Step 1: Create the job
      const jobCreation = await realChatApi.createQueryJob(request);
      const jobId = jobCreation.job_id;

      // Step 2: Poll until response is ready
      const startTime = Date.now();
      let currentInterval = 2000;
      let consecutiveFailures = 0;
      const maxTimeout = 600000 * 2;
      let responseReadyResult: ChatResponse | null = null;
      let citationsReadyResult: ChatResponse | null = null;
      let responseReadyTimestamp: number | null = null;
      const citationPhaseTimeout = 180000; // 3 min max wait for citations after response_ready

      while (Date.now() - startTime < maxTimeout) {
        if (signal?.aborted) {
          throw new DOMException('Polling was aborted', 'AbortError');
        }

        // Safety: if we have the response but citations are taking too long, stop polling
        if (responseReadyResult && responseReadyTimestamp && 
            Date.now() - responseReadyTimestamp > citationPhaseTimeout) {
          console.log(`Citation phase timed out after ${citationPhaseTimeout}ms, returning response without citations`);
          return responseReadyResult;
        }

        try {
          const status = await realChatApi.getJobStatus(jobId, signal);
          consecutiveFailures = 0;

          if (onProgress) {
            try {
              onProgress(status);
            } catch (progressError) {
              console.warn('Progress callback error in two-phase response:', progressError);
            }
          }

          // Phase 1: Response ready - return immediately and continue in background
          if (status.status === 'response_ready' && !responseReadyResult) {
            responseReadyResult = await realChatApi.getJobResult(jobId, signal);
            responseReadyTimestamp = Date.now();
            console.log('Response ready, calling onResponseReady callback');
            
            if (onResponseReady) {
              onResponseReady(responseReadyResult);
            }

            currentInterval = Math.max(3000, 2000);
          }
          
          // Phase 2: Citations ready - update with citations, continue for search enrichment
          if (status.status === 'citations_ready' && !citationsReadyResult) {
            citationsReadyResult = await realChatApi.getJobResult(jobId, signal);
            console.log('Citations ready, calling onCitationsReady callback (continuing for search enrichment)');
            
            if (onCitationsReady) {
              onCitationsReady(citationsReadyResult);
            }

            currentInterval = Math.max(3000, 2000);
          }

          // Phase 3: Fully completed (with search enrichment)
          if (status.status === 'completed') {
            const finalResult = await realChatApi.getJobResult(jobId, signal);
            console.log('All processing completed (including search enrichment), calling onCitationsReady callback with final result');
            
            if (onCitationsReady) {
              onCitationsReady(finalResult);
            }
            
            return finalResult;
          }

          if (status.status === 'failed' || status.status === 'cancelled') {
            const errorMessage = status.status === 'cancelled' 
              ? 'Task was cancelled by user'
              : (status.error || 'Job failed with unknown error');
            throw new Error(errorMessage);
          }

          // Adaptive polling
          const elapsedTime = Date.now() - startTime;
          if (status.current_stage === 'sql' && elapsedTime > 60000) {
            currentInterval = Math.min(5000, currentInterval * 1.1);
          } else if (elapsedTime > 300000) {
            currentInterval = Math.min(10000, currentInterval * 1.2);
          } else if (status.status === 'response_ready' || status.status === 'citations_ready') {
            currentInterval = Math.max(3000, 2000);
          } else {
            currentInterval = 2000;
          }

          const delayMs = currentInterval;
          await new Promise(resolve => setTimeout(resolve, delayMs));

        } catch (error: any) {
          if (error.name === 'AbortError') {
            throw error;
          }
          
          consecutiveFailures++;
          if (consecutiveFailures >= 5) {
            throw new Error(`Polling failed after ${consecutiveFailures} consecutive errors: ${error}`);
          }
          
          const backoffMs = Math.min(30000, 2000 * Math.pow(2, consecutiveFailures));
          await new Promise(resolve => setTimeout(resolve, backoffMs));
        }
      }

      // If we have a response but timed out waiting for completion, return what we have
      if (responseReadyResult) {
        console.log('Overall polling timed out but we have response_ready result, returning it');
        return responseReadyResult;
      }
      throw new Error('Job polling timed out');
    } catch (error) {
      console.error('Error in two-phase polling workflow:', error);
      throw error;
    }
  },

  // Helper method to poll for citation updates after response is ready
  pollForCitationsUpdate: async (
    jobId: string,
    initialResponse: ChatResponse,
    signal?: AbortSignal
  ): Promise<ChatResponse | null> => {
    try {
      const startTime = Date.now();
      const maxTimeout = 300000; // 5 minutes for citations
      let currentInterval = 3000; // Slower polling

      while (Date.now() - startTime < maxTimeout) {
        if (signal?.aborted) {
          console.log('Citation polling aborted');
          return null;
        }

        try {
          const status = await realChatApi.getJobStatus(jobId, signal);

          if (status.status === 'completed') {
            const finalResult = await realChatApi.getJobResult(jobId, signal);
            console.log('Citations completed, updated result available');
            return finalResult;
          }

          if (status.status === 'failed') {
            console.warn('Citation processing failed, keeping original response');
            return null;
          }

          await new Promise(resolve => setTimeout(resolve, currentInterval));

        } catch (error) {
          console.warn('Citation polling error:', error);
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
      }

      console.log('Citation polling timed out');
      return null;
    } catch (error) {
      console.error('Error in citation polling:', error);
      return null;
    }
  },

  // Legacy sendMessage method that uses polling internally
  sendMessage: async (
    request: ChatRequest, 
    onProgress?: ProgressCallback,
    signal?: AbortSignal
  ): Promise<ChatResponse> => {
    return realChatApi.sendMessageWithPolling(request, onProgress, signal);
  },

  // Provide clarification for a previous query
  provideClarification: async (
    request: ChatRequest,
    onProgress?: ProgressCallback,
    signal?: AbortSignal
  ): Promise<ChatResponse> => {
    try {
      const response = await apiClient.post<JobCreationResponse>('/clarify', {
        message: request.message,
        conversation_id: request.conversationId,
        original_query: request.original_query,
        is_clarification: request.is_clarification || true,
        previous_user_message: request.previous_user_message,
        previous_assistant_response: request.previous_assistant_response,
        user_email: request.user_email,  // Pass user_email for logging
        enable_search_enrichment: request.enable_search_enrichment || false  // Dev toggle for search enrichment
      });
      
      // Poll until completion
      return await realChatApi.pollJobUntilComplete(
        response.data.job_id,
        onProgress,
        2000,
        600000*2,
        signal
      );
      
    } catch (error) {
      console.error('Error sending clarification:', error);
      throw error;
    }
  },

  // Get database schema for documentation
  getSchema: async (): Promise<any> => {
    try {
      const response = await apiClient.get('/schema');
      return response.data;
    } catch (error) {
      console.error('Error fetching schema:', error);
      throw error;
    }
  },

  // Health check
  checkHealth: async (): Promise<boolean> => {
    try {
      const response = await apiClient.get('/health');
      return response.data.status === 'healthy';
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  },

  // Get schema cache status
  getSchemaStatus: async (): Promise<SchemaStatusResponse> => {
    try {
      const response = await apiClient.get<SchemaStatusResponse>('/schema/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching schema status:', error);
      throw error;
    }
  },

  // Trigger a schema cache refresh
  refreshSchemaCache: async (): Promise<SchemaRefreshResponse> => {
    try {
      const response = await apiClient.post<SchemaRefreshResponse>('/schema/refresh');
      return response.data;
    } catch (error) {
      console.error('Error refreshing schema cache:', error);
      throw error;
    }
  },

  // Feedback methods
  submitFeedback: async (payload: FeedbackPayload): Promise<{ status: string; feedback_id: number; job_id: string }> => {
    try {
      const response = await apiClient.post('/feedback', payload);
      return response.data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  },

  checkFeedback: async (jobId: string, email: string): Promise<{ has_feedback: boolean; overall_rating?: boolean }> => {
    try {
      const response = await apiClient.get(`/feedback/check/${jobId}`, { params: { email } });
      return response.data;
    } catch (error) {
      console.warn('Error checking feedback:', error);
      return { has_feedback: false };
    }
  },

  // DEPRECATED: streamMessage is replaced by sendMessage with polling
  // Keeping for backward compatibility but implementing with polling
  streamMessage: async (
    request: ChatRequest, 
    onChunk: (chunk: any) => void, 
    onError?: (error: any) => void, 
    onComplete?: () => void
  ): Promise<void> => {
    try {
      console.warn('streamMessage is deprecated. Using polling implementation instead.');
      
      const result = await realChatApi.sendMessage(request, (status) => {
        // Convert status to chunk format for backward compatibility
        onChunk({
          type: 'stage_update',
          stage: status.current_stage,
          message: status.stage_message,
          progress: status.progress
        });
      });
      
      // Send final result
      onChunk({
        type: 'final_result',
        data: result
      });
      
      if (onComplete) onComplete();
      
    } catch (error) {
      console.error('Error in streaming message (polling implementation):', error);
      if (onError) onError(error);
      throw error;
    }
  },
};

// Dynamic API that switches between real and mock based on configuration
// This is exported as chatApi for use throughout the application
export const chatApi = {
  _getImplementation: () => {
    // In production, always use real API
    if (process.env.NODE_ENV === 'production') {
      return realChatApi;
    }
    
    // Check if useMockApi is set in localStorage (development only)
    const useMockApi = localStorage.getItem('useMockApi') === 'true';
    return useMockApi ? mockChatApi : realChatApi;
  },
  
  // New polling-based methods
  createQueryJob: async (request: ChatRequest): Promise<JobCreationResponse> => {
    return realChatApi.createQueryJob(request);
  },
  
  getJobStatus: async (jobId: string, signal?: AbortSignal): Promise<JobStatus> => {
    return realChatApi.getJobStatus(jobId, signal);
  },
  
  getJobResult: async (jobId: string, signal?: AbortSignal): Promise<ChatResponse> => {
    return realChatApi.getJobResult(jobId, signal);
  },
  
  pollJobUntilComplete: async (
    jobId: string, 
    onProgress?: ProgressCallback,
    pollInterval?: number,
    maxTimeout?: number,
    signal?: AbortSignal
  ): Promise<ChatResponse> => {
    return realChatApi.pollJobUntilComplete(jobId, onProgress, pollInterval, maxTimeout, signal);
  },
  
  cancelJob: async (jobId: string): Promise<boolean> => {
    return chatApi._getImplementation().cancelJob(jobId);
  },
  
  // Updated to use polling with progress callbacks
  sendMessage: async (
    request: ChatRequest, 
    onProgress?: ProgressCallback,
    signal?: AbortSignal
  ): Promise<ChatResponse> => {
    return chatApi._getImplementation().sendMessage(request, onProgress, signal);
  },

  // Two-phase response method
  sendMessageWithTwoPhaseResponse: async (
    request: ChatRequest, 
    onProgress?: ProgressCallback,
    onResponseReady?: (response: ChatResponse) => void,
    onCitationsReady?: (response: ChatResponse) => void,
    signal?: AbortSignal
  ): Promise<ChatResponse> => {
    return realChatApi.sendMessageWithTwoPhaseResponse(request, onProgress, onResponseReady, onCitationsReady, signal);
  },
  
  provideClarification: async (
    request: ChatRequest,
    onProgress?: ProgressCallback,
    signal?: AbortSignal
  ): Promise<ChatResponse> => {
    return chatApi._getImplementation().provideClarification(request, onProgress, signal);
  },
  
  getSchema: async (): Promise<any> => {
    return chatApi._getImplementation().getSchema();
  },
  
  checkHealth: async (): Promise<boolean> => {
    return chatApi._getImplementation().checkHealth();
  },

  getSchemaStatus: async (): Promise<SchemaStatusResponse> => {
    return realChatApi.getSchemaStatus();
  },

  refreshSchemaCache: async (): Promise<SchemaRefreshResponse> => {
    return realChatApi.refreshSchemaCache();
  },

  submitFeedback: async (payload: FeedbackPayload): Promise<{ status: string; feedback_id: number; job_id: string }> => {
    return realChatApi.submitFeedback(payload);
  },

  checkFeedback: async (jobId: string, email: string): Promise<{ has_feedback: boolean; overall_rating?: boolean }> => {
    return realChatApi.checkFeedback(jobId, email);
  },

  // DEPRECATED: streamMessage is replaced by sendMessage with polling
  // Keeping for backward compatibility
  streamMessage: async (
    request: ChatRequest, 
    onChunk: (chunk: any) => void, 
    onError?: (error: any) => void, 
    onComplete?: () => void
  ): Promise<void> => {
    return chatApi._getImplementation().streamMessage(request, onChunk, onError, onComplete);
  }
};

// Export additional types for convenience
export type { ChatRequest, ChatResponse };
